import React, { useEffect, useState } from 'react';
import { clientSyncService } from '../../_services/clientSync.service';

/**
 * Merge clients — content-only view, designed to live INSIDE the client
 * slide panel. The panel supplies the back-arrow header; this renders the
 * survivor choice, the field picks, a booking-history summary and the
 * pinned confirm footer.
 *
 * Which record SURVIVES is chosen inline (the two cards at the top), so
 * the roles can be swapped without a separate header control. The safety
 * matrix runs server-side via /v2/user-merge/preview; confirm triggers the
 * transactional merge via /v2/user-merge/confirm (72h rollback window).
 *
 * Spec: docs/client-flows.md §7.2
 */

const ink = '#29282D';
const teal = '#007EA0';
const muted = '#8E8E93';
const softText = '#6B7280';
const line = '#ECECF0';

const FIELDS = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone' },
    { key: 'dob', label: 'Date of birth' },
];

const defaultPicks = (winner, loser) => {
    const p = {};
    FIELDS.forEach(({ key }) => {
        // Default to whichever side actually has a value so a merge never
        // blanks a detail the surviving record was missing.
        p[key] = (!winner[key] && loser[key]) ? 'loser' : 'winner';
    });
    return p;
};

const MergeClientsView = ({ winnerUserId: initialWinner, loserUserId: initialLoser, onBack, onConfirmed, canSwap = true }) => {
    // The two people are stable; only which one survives changes.
    const [personA] = useState(initialWinner);
    const [personB] = useState(initialLoser);
    const [survivorId, setSurvivorId] = useState(initialWinner);
    const winnerUserId = survivorId;
    const loserUserId = survivorId === personA ? personB : personA;

    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState('');
    const [picks, setPicks] = useState({ name: 'winner', email: 'winner', phoneNumber: 'winner', dob: 'winner' });
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        if (!loserUserId || !winnerUserId) return;
        setLoading(true);
        setError('');
        clientSyncService.previewMerge({ loserUserId, winnerUserId })
            .then(res => {
                // Error envelopes come back as HTTP 200 {statusCode:0}.
                if (res?.data?.statusCode === 0) {
                    setError(res?.data?.error?.responseMessage || 'Failed to load merge preview');
                    setLoading(false);
                    return;
                }
                const data = res?.data?.responseData || res?.data?.data;
                setPreview(data);
                setPicks(defaultPicks(data?.winner || {}, data?.loser || {}));
                setLoading(false);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || 'Failed to load merge preview');
                setLoading(false);
            });
    }, [loserUserId, winnerUserId]);

    const chooseSurvivor = (id) => {
        if (id === survivorId || !canSwap) return;
        setSurvivorId(id); // refetch + fresh defaults happen in the effect
    };

    const handleConfirm = () => {
        if (!preview || confirming) return;
        setConfirming(true);
        const fieldChoices = {};
        const winner = preview.winner || {};
        const loser = preview.loser || {};
        for (const field of ['name', 'email', 'phoneNumber', 'dob']) {
            const sideObj = picks[field] === 'winner' ? winner : loser;
            if (sideObj[field]) fieldChoices[field] = sideObj[field];
        }
        clientSyncService.confirmMerge({ loserUserId, winnerUserId, fieldChoices })
            .then(res => {
                if (res?.data?.statusCode === 0) {
                    setError(res?.data?.error?.responseMessage || 'Merge failed');
                    setConfirming(false);
                    return;
                }
                const data = res?.data?.responseData || res?.data?.data;
                setConfirming(false);
                if (onConfirmed) onConfirmed(data);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || 'Merge failed');
                setConfirming(false);
            });
    };

    const winner = preview?.winner || {};
    const loser = preview?.loser || {};
    const allowed = preview?.safety?.allowed;
    const dataFor = (id) => (id === survivorId ? winner : loser);
    const bookingsFor = (id) => (id === survivorId ? (preview?.winnerBookingCount || 0) : (preview?.loserBookingCount || 0));
    const totalBookings = (preview?.winnerBookingCount || 0) + (preview?.loserBookingCount || 0);
    const affected = preview?.affectedSalonCount || 0;

    const SurvivorCard = ({ id }) => {
        const selected = id === survivorId;
        const d = dataFor(id);
        const count = bookingsFor(id);
        return (
            <button
                type="button"
                onClick={() => chooseSurvivor(id)}
                disabled={!canSwap && !selected}
                style={{
                    flex: 1, minWidth: 0, textAlign: 'left', fontFamily: 'inherit',
                    cursor: canSwap ? 'pointer' : 'default', padding: '14px 16px', borderRadius: 12, background: '#fff',
                    border: selected ? `2px solid ${teal}` : `1px solid ${line}`,
                    boxShadow: selected ? '0 0 0 3px rgba(0,126,160,0.10)' : 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box', border: selected ? `5px solid ${teal}` : `2px solid #CFCFD4`, background: '#fff' }} />
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || 'Unnamed client'}</div>
                        <div style={{ fontSize: 12.5, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.email || 'No email'}</div>
                    </div>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: muted }}>
                    {count} booking{count === 1 ? '' : 's'}
                    {selected ? <span style={{ color: teal, fontWeight: 700 }}> · Kept as the main record</span> : null}
                </div>
            </button>
        );
    };

    const FieldOption = ({ field, side, value }) => {
        const selected = picks[field] === side;
        const empty = !value;
        return (
            <button
                type="button"
                onClick={() => setPicks(p => ({ ...p, [field]: side }))}
                style={{
                    flex: 1, minWidth: 0, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 11px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit',
                    border: selected ? `1.5px solid ${teal}` : `1px solid ${line}`,
                    background: selected ? 'rgba(0,126,160,0.05)' : '#fff',
                }}
            >
                <span style={{ width: 15, height: 15, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box', border: selected ? `4.5px solid ${teal}` : `1.5px solid #CFCFD4`, background: '#fff' }} />
                <span style={{ fontSize: 13.5, color: empty ? muted : ink, fontStyle: empty ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || 'Not set'}</span>
            </button>
        );
    };

    const renderField = ({ key, label }) => {
        const w = winner[key];
        const l = loser[key];
        if (!w && !l) return null; // neither record has this, nothing to merge
        const same = w && l && w === l;
        return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: `1px solid ${line}` }}>
                <div style={{ width: 96, flexShrink: 0, fontWeight: 600, color: softText, fontSize: 12.5 }}>{label}</div>
                {same ? (
                    <div style={{ flex: 1, fontSize: 14, color: ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w}</div>
                ) : (
                    <div style={{ flex: 1, display: 'flex', gap: 10, minWidth: 0 }}>
                        <FieldOption field={key} side="winner" value={dataFor(survivorId)[key]} />
                        <FieldOption field={key} side="loser" value={dataFor(loserUserId)[key]} />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
                {loading && <p style={{ color: muted, fontSize: 14 }}>Loading preview…</p>}
                {error && (
                    <div style={{ padding: 12, background: '#FDECEA', color: '#A8071A', borderRadius: 10, marginBottom: 16, fontSize: 14 }}>{error}</div>
                )}

                {preview && !error && (
                    <>
                        {!allowed && preview.safety && (
                            <div style={{ padding: 12, background: '#FDECEA', color: '#A8071A', borderRadius: 10, marginBottom: 16, fontSize: 14 }}>
                                <strong>Merge blocked:</strong> {preview.safety.reason}
                            </div>
                        )}

                        <p style={{ color: softText, fontSize: 13.5, lineHeight: 1.55, margin: '0 0 18px' }}>
                            Merging combines two client records into one. Choose which record to keep, then which details to carry over. You can undo this within 72 hours.
                        </p>

                        {/* 1 — which record survives */}
                        <div style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                            Which record to keep
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <SurvivorCard id={personA} />
                            <SurvivorCard id={personB} />
                        </div>
                        {canSwap && (
                            <p style={{ fontSize: 12, color: muted, margin: '8px 2px 0' }}>
                                The kept record holds the login and account. Tap the other card to switch.
                            </p>
                        )}

                        {/* 2 — details to carry over */}
                        <div style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '22px 0 4px' }}>
                            Details to keep
                        </div>
                        {FIELDS.map(renderField)}

                        {/* 3 — booking history + rewards */}
                        <div style={{ marginTop: 18, padding: '14px 16px', borderRadius: 12, background: '#F7F7F8', border: `1px solid ${line}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
                                </svg>
                                <span style={{ fontSize: 13.5, fontWeight: 700, color: ink }}>Booking history &amp; rewards</span>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: softText, lineHeight: 1.55 }}>
                                All {totalBookings} booking{totalBookings === 1 ? '' : 's'} from both records ({preview.winnerBookingCount || 0} + {preview.loserBookingCount || 0}) move onto the kept record, along with any loyalty points, stamps and rewards. Nothing is lost.
                            </p>
                        </div>

                        {affected > 0 && (
                            <p style={{ marginTop: 12, fontSize: 12.5, color: softText, lineHeight: 1.55 }}>
                                This client is also on the books at {affected} other salon{affected === 1 ? '' : 's'}, who will see the updated record.
                            </p>
                        )}
                    </>
                )}
            </div>

            <div style={{ padding: '14px 24px', borderTop: `1px solid ${line}`, background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
                <button type="button" className="cdp-btn-cancel" onClick={onBack} disabled={confirming}>
                    Cancel
                </button>
                <button type="button" className="cdp-btn-save" onClick={handleConfirm} disabled={confirming || !allowed}>
                    {confirming ? 'Merging…' : 'Confirm merge'}
                </button>
            </div>
        </div>
    );
};

export default MergeClientsView;
